import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HiOutlineMail,
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle,
  HiOutlineArrowLeft
} from 'react-icons/hi';
import ThemeToggle from '@/components/common/ThemeToggle';
import adminUsersService from '@/services/adminUsersService';
import logo from './../assets/logo.svg';

function ForgotPasswordPage(): JSX.Element {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const showError = touched && email.trim() !== '' && !isValidEmail;
  const showValid = touched && isValidEmail;
  const isEmpty = touched && email.trim() === '';

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setTouched(true);
    setError('');

    if (!email.trim()) {
      setError('El correo electrónico es requerido');
      return;
    }

    if (!isValidEmail) {
      setError('Ingresa un correo electrónico válido');
      return;
    }

    setIsSubmitting(true);

    try {
      await adminUsersService.forgotPassword({email});
      setEmailSent(true);
    } catch {
      setError('No se pudo enviar el correo. Intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  
  if (emailSent) {
    return (
      <div className="login-page">
        <ThemeToggle />
        <div className="login-container fade-in">
          <div className="login-card">
            <div className="login-header text-center">
              <img src={logo} className="img-fluid mb-3" alt="Logo" />

              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: 'rgba(122, 185, 0, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  color: 'var(--secondary)',
                  fontSize: '2rem',
                }}
              >
                <HiOutlineCheckCircle />
              </div>

              <h1 className="login-title">Revisa tu correo</h1>
              <p className="login-subtitle" style={{ maxWidth: '340px', margin: '0.5rem auto 0' }}>
                Si existe una cuenta asociada a <strong>{email}</strong>, recibirás un enlace para restablecer tu contraseña.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1.5rem' }}>
              <button
                type="button"
                className="btn btn-primary btn-lg btn-block"
                onClick={() => {
                  setEmailSent(false);
                  setEmail('');
                  setTouched(false);
                  setError('');
                }}
              >
                Enviar de nuevo
              </button>

              <Link
                to="/login"
                className="btn btn-block"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '0.75rem',
                  background: 'transparent',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-secondary)',
                  fontWeight: 500,
                  fontSize: '0.9375rem',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                <HiOutlineArrowLeft />
                Volver al inicio de sesión
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Vista del formulario ──
  return (
    <div className="login-page">
      <ThemeToggle />

      <div className="login-container fade-in">
        <div className="login-card">
          {/* Header */}
          <div className="login-header text-center">
            <img src={logo} className="img-fluid mb-3" alt="Logo" />
            <h1 className="login-title">Recuperar contraseña</h1>
            <p className="login-subtitle">
              Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <div
              className="alert alert-danger d-flex align-items-center gap-2 mb-4 fade-in"
              role="alert"
            >
              <HiOutlineExclamationCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={(e) => { void handleSubmit(e); }} noValidate>
            {/* Email Field */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Correo electrónico
              </label>
              <div className="input-group">
                <span className="input-icon">
                  <HiOutlineMail />
                </span>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className={`form-control has-icon-left ${
                    showValid ? 'is-valid' : ''
                  } ${showError || isEmpty ? 'is-invalid' : ''}`}
                  placeholder="correo@ejemplo.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError('');
                  }}
                  onBlur={() => setTouched(true)}
                  autoComplete="email"
                  autoFocus
                  disabled={isSubmitting}
                />
                {showValid && (
                  <span
                    className="input-icon input-icon-right"
                    style={{ color: 'var(--secondary)' }}
                  >
                    <HiOutlineCheckCircle />
                  </span>
                )}
              </div>
              {isEmpty && (
                <p className="form-text error fade-in">
                  <HiOutlineExclamationCircle style={{ marginRight: 4 }} />
                  El correo electrónico es requerido
                </p>
              )}
              {showError && (
                <p className="form-text error fade-in">
                  <HiOutlineExclamationCircle style={{ marginRight: 4 }} />
                  Ingresa un correo electrónico válido
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="btn btn-primary btn-lg btn-block"
              disabled={isSubmitting}
              style={{ marginTop: '0.5rem' }}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner" />
                  <span>Enviando...</span>
                </>
              ) : (
                'Enviar enlace de recuperación'
              )}
            </button>
          </form>

          {/* Back to login */}
          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <Link
              to="/login"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                color: 'var(--primary)',
                fontSize: '0.875rem',
                fontWeight: 500,
                textDecoration: 'none',
              }}
            >
              <HiOutlineArrowLeft />
              Volver al inicio de sesión
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;